import { DishCustomize, DishHeading, DishOrderControls } from "./dish-detail-parts"
import { DishIllustration } from "./dish-illustration"
import { useDishOrder } from "./use-dish-order"
import { usePickupHours } from "./use-pickup-hours"
import type { ItemDetail, ServiceContext } from "./types"
import { t } from "@/lib/i18n"

type DishDetailProps = {
  item: ItemDetail
  billId: number | null
  serviceContext?: ServiceContext
}

/**
 * Deep-link page for one dish. In-app selection opens `DishDetailDrawer`, which
 * shows the same `DishHeading` and `DishCustomize` — this shell exists for a URL
 * somebody pasted, not for a tap.
 *
 * It carries no back control. It used to, because the route declared
 * `shell_mode :takeover` and a takeover gets no navigation from the shell, which
 * left the screen a dead end that had to rescue itself with `history.back()`. The
 * route is an ordinary one now (`:visit` / `:pickup`), so the tab bar is there
 * like it is on the menu and the cart, and it is the only way out anyone needs.
 */
export function DishDetail({ item, billId, serviceContext = "dine_in" }: DishDetailProps) {
  const isPickup = serviceContext === "pickup"
  const order = useDishOrder(item, isPickup ? "pick_up" : "eat_in")
  const { closed: pickupClosed } = usePickupHours()
  const hasPhoto = Boolean(item.imageUrl)
  const unavailable = item.available === false

  const heading = (
    <DishHeading
      name={item.name}
      emoji={item.emoji}
      showEmoji={!hasPhoto}
      price={order.basePrice}
      originalPrice={order.originalPrice}
      promotionName={order.promotion?.name}
      currency={item.currency}
      description={item.description}
    />
  )

  const customize = (
    <div className="mt-6">
      <DishCustomize
        groups={order.groups}
        currency={item.currency}
        selectedIds={order.selectedIds}
        toggleExtra={order.toggleExtra}
        clearGroup={order.clearGroup}
        note={order.note}
        setNote={order.setNote}
        serviceType={isPickup ? undefined : order.serviceType}
        onServiceTypeChange={isPickup ? undefined : order.setServiceType}
      />
    </div>
  )

  return (
    <div className="relative flex min-h-full flex-col text-foreground" data-testid="dish-detail-view">
      {hasPhoto ? (
        <div className="mx-auto w-full max-w-5xl lg:grid lg:grid-cols-2 lg:gap-10 lg:px-6 lg:py-6">
          <div className="lg:sticky lg:top-6 lg:self-start">
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted lg:aspect-square lg:rounded-2xl lg:border">
              <DishIllustration src={item.imageUrl} alt={item.name} />
            </div>
          </div>
          <div className="px-4 pb-6 pt-5 lg:px-0 lg:pt-0">
            {heading}
            {customize}
          </div>
        </div>
      ) : (
        // Same max-width as the order panel below, so the title and the price it
        // repeats sit on one column. They used to be max-w-2xl over max-w-5xl,
        // which read as a layout bug on desktop and was one.
        <div className="mx-auto w-full max-w-5xl px-4 pb-6 pt-5">
          {heading}
          {customize}
        </div>
      )}

      <div className="sticky bottom-0 mt-auto border-t bg-background pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3">
        <div className="mx-auto w-full max-w-5xl px-4">
          {unavailable && (
            <p
              data-testid="dish-unavailable-notice"
              className="mb-3 rounded-lg bg-muted px-3 py-2 text-center text-xs font-medium text-muted-foreground"
            >
              {t("ui.user.dish.unavailable_reason")}
            </p>
          )}
          <DishOrderControls
            qty={order.qty}
            setQty={order.setQty}
            total={order.total}
            currency={item.currency}
            blockedReason={
              // Closed first: it is the reason the diner can do something
              // about, and on a pickup screen there is no table to lack.
              pickupClosed ? "closed" : !order.usesLocalCart && billId === null ? "no_table" : null
            }
            submitting={order.submitting}
            canSubmit={order.canSubmit}
            unavailable={unavailable}
            onAdd={() => order.submit()}
          />
        </div>
      </div>
    </div>
  )
}
